import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../api/axiosInstance';

const BASE_URL = '/api/inventory';

// Store Purchases - Single API call
export const fetchStorePurchasesData = createAsyncThunk(
  'storePurchases/fetchStorePurchasesData',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/purchases/store-purchases/`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Suppliers
export const fetchSuppliers = createAsyncThunk(
  'storePurchases/fetchSuppliers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/suppliers/`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createSupplier = createAsyncThunk(
  'storePurchases/createSupplier',
  async (supplierData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BASE_URL}/suppliers/`, supplierData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateSupplier = createAsyncThunk(
  'storePurchases/updateSupplier',
  async ({ id, ...supplierData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(`${BASE_URL}/suppliers/${id}/`, supplierData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteSupplier = createAsyncThunk(
  'storePurchases/deleteSupplier',
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`${BASE_URL}/suppliers/${id}/`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Purchases
export const fetchPurchases = createAsyncThunk(
  'storePurchases/fetchPurchases',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/purchases/`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createPurchase = createAsyncThunk(
  'storePurchases/createPurchase',
  async (purchaseData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BASE_URL}/purchases/`, purchaseData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updatePurchase = createAsyncThunk(
  'storePurchases/updatePurchase',
  async ({ id, ...purchaseData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(`${BASE_URL}/purchases/${id}/`, purchaseData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deletePurchase = createAsyncThunk(
  'storePurchases/deletePurchase',
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`${BASE_URL}/purchases/${id}/`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getPurchaseSummary = createAsyncThunk(
  'storePurchases/getPurchaseSummary',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/purchases/summary/`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Purchase Payments
export const fetchPurchasePayments = createAsyncThunk(
  'storePurchases/fetchPurchasePayments',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/purchase-payments/`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createPurchasePayment = createAsyncThunk(
  'storePurchases/createPurchasePayment',
  async (paymentData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BASE_URL}/purchase-payments/`, paymentData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getDailyPayments = createAsyncThunk(
  'storePurchases/getDailyPayments',
  async (date, { rejectWithValue }) => {
    try {
      const url = date 
        ? `${BASE_URL}/purchase-payments/daily-payments/?date=${date}`
        : `${BASE_URL}/purchase-payments/daily-payments/`;
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getBenefits = createAsyncThunk(
  'storePurchases/getBenefits',
  async ({ dateFrom, dateTo }, { rejectWithValue }) => {
    try {
      let url = `${BASE_URL}/purchase-payments/benefits/`;
      const params = [];
      if (dateFrom) params.push(`date_from=${dateFrom}`);
      if (dateTo) params.push(`date_to=${dateTo}`);
      if (params.length > 0) url += '?' + params.join('&');
      
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Store Inventory
export const fetchStoreInventory = createAsyncThunk(
  'storePurchases/fetchStoreInventory',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/store-inventory/`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const recordInventoryUsage = createAsyncThunk(
  'storePurchases/recordInventoryUsage',
  async ({ id, quantity, notes }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BASE_URL}/store-inventory/${id}/use/`, {
        quantity,
        notes
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const adjustInventoryStock = createAsyncThunk(
  'storePurchases/adjustInventoryStock',
  async ({ id, quantity, notes }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BASE_URL}/store-inventory/${id}/adjust/`, {
        quantity,
        notes
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const addProduction = createAsyncThunk(
  'storePurchases/addProduction',
  async ({ item_name, quantity, purchase_price, sale_price, notes }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BASE_URL}/store-inventory/add-production/`, {
        item_name,
        quantity,
        purchase_price,
        sale_price,
        notes
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Sales
export const fetchSales = createAsyncThunk(
  'storePurchases/fetchSales',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/sales/`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createSale = createAsyncThunk(
  'storePurchases/createSale',
  async (saleData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BASE_URL}/sales/`, saleData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteSale = createAsyncThunk(
  'storePurchases/deleteSale',
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`${BASE_URL}/sales/${id}/`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const storePurchasesSlice = createSlice({
  name: 'storePurchases',
  initialState: {
    suppliers: [],
    purchases: [],           // student purchases (income, supplier=null)
    supplierPurchases: [],   // supplier purchases (outcome, supplier set)
    payments: [],
    sales: [],
    inventory: [],
    summary: null,
    dailyPayments: null,
    benefits: null,
    loading: false,
    saving: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Store Purchases Data (Single API)
      .addCase(fetchStorePurchasesData.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchStorePurchasesData.fulfilled, (state, action) => {
        state.loading = false;
        const allPurchases = action.payload.purchases || [];
        // Backend may return pre-split lists or a single combined list
        if (action.payload.supplier_purchases !== undefined) {
          state.purchases = allPurchases;
          state.supplierPurchases = action.payload.supplier_purchases;
        } else {
          // Derive the split client-side from the combined list
          state.purchases = allPurchases.filter(p => !p.authorized_payer);
          state.supplierPurchases = allPurchases.filter(p => !!p.authorized_payer);
        }
        state.suppliers = action.payload.suppliers || [];
        state.payments = action.payload.payments || [];
        state.sales = action.payload.sales || [];
        state.summary = action.payload.summary || null;
        state.dailyPayments = action.payload.dailyPayments || null;
        state.benefits = action.payload.benefits || null;
        state.inventory = action.payload.inventory || [];
      })
      .addCase(fetchStorePurchasesData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Suppliers
      .addCase(fetchSuppliers.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSuppliers.fulfilled, (state, action) => {
        state.loading = false;
        state.suppliers = action.payload;
      })
      .addCase(fetchSuppliers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createSupplier.fulfilled, (state, action) => {
        state.suppliers.push(action.payload);
      })
      .addCase(updateSupplier.fulfilled, (state, action) => {
        const index = state.suppliers.findIndex(s => s.id === action.payload.id);
        if (index !== -1) state.suppliers[index] = action.payload;
      })
      .addCase(deleteSupplier.fulfilled, (state, action) => {
        state.suppliers = state.suppliers.filter(s => s.id !== action.payload);
      })
      // Purchases
      .addCase(fetchPurchases.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchPurchases.fulfilled, (state, action) => {
        state.loading = false;
        state.purchases = action.payload;
      })
      .addCase(fetchPurchases.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createPurchase.pending, (state) => {
        state.saving = true;
      })
      .addCase(createPurchase.fulfilled, (state, action) => {
        state.saving = false;
        if (action.payload.authorized_payer) {
          state.supplierPurchases.unshift(action.payload);
        } else {
          state.purchases.unshift(action.payload);
        }
      })
      .addCase(createPurchase.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      .addCase(updatePurchase.fulfilled, (state, action) => {
        const index = state.purchases.findIndex(p => p.id === action.payload.id);
        if (index !== -1) state.purchases[index] = action.payload;
      })
      .addCase(deletePurchase.fulfilled, (state, action) => {
        state.purchases = state.purchases.filter(p => p.id !== action.payload);
        state.supplierPurchases = state.supplierPurchases.filter(p => p.id !== action.payload);
      })
      .addCase(getPurchaseSummary.fulfilled, (state, action) => {
        state.summary = action.payload;
      })
      // Payments
      .addCase(fetchPurchasePayments.fulfilled, (state, action) => {
        state.payments = action.payload;
      })
      .addCase(createPurchasePayment.fulfilled, (state, action) => {
        state.payments.push(action.payload);
        // Update purchase payment status
        const purchase = state.purchases.find(p => p.id === action.payload.purchase);
        if (purchase) {
          purchase.payments = purchase.payments || [];
          purchase.payments.push(action.payload);
        }
      })
      .addCase(getDailyPayments.fulfilled, (state, action) => {
        state.dailyPayments = action.payload;
      })
      .addCase(getBenefits.fulfilled, (state, action) => {
        state.benefits = action.payload;
      })
      // Sales
      .addCase(fetchSales.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSales.fulfilled, (state, action) => {
        state.loading = false;
        state.sales = action.payload;
      })
      .addCase(fetchSales.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createSale.pending, (state) => {
        state.saving = true;
      })
      .addCase(createSale.fulfilled, (state, action) => {
        state.saving = false;
        state.sales.unshift(action.payload);
      })
      .addCase(createSale.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      .addCase(deleteSale.fulfilled, (state, action) => {
        state.sales = state.sales.filter(s => s.id !== action.payload);
      })
      // Inventory
      .addCase(fetchStoreInventory.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchStoreInventory.fulfilled, (state, action) => {
        state.loading = false;
        state.inventory = action.payload;
      })
      .addCase(fetchStoreInventory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(recordInventoryUsage.fulfilled, (state, action) => {
        const updatedItem = action.payload.inventory;
        const index = state.inventory.findIndex(i => i.id === updatedItem.id);
        if (index !== -1) {
          state.inventory[index] = updatedItem;
        }
      })
      .addCase(adjustInventoryStock.fulfilled, (state, action) => {
        const updatedItem = action.payload.inventory;
        const index = state.inventory.findIndex(i => i.id === updatedItem.id);
        if (index !== -1) {
          state.inventory[index] = updatedItem;
        }
      })
      .addCase(addProduction.fulfilled, (state, action) => {
        const updatedItem = action.payload.inventory;
        const index = state.inventory.findIndex(i => i.id === updatedItem.id);
        if (index !== -1) {
          state.inventory[index] = updatedItem;
        } else {
          state.inventory.push(updatedItem);
        }
      });
  },
});

export const { clearError } = storePurchasesSlice.actions;
export default storePurchasesSlice.reducer;
